import ApplicationController from "./application_controller"

export default class extends ApplicationController {
  static targets = ["alert", "firstName", "lastName", "email", "submit"]

  static values = {
    requiredEmail: Boolean,
    verifyUrl: String,
  }

  connect() {
    this.lookupTimer = null
    this.requestToken = 0
    this.verifiedEmail = "" // email value the name fields currently reflect
    this.namesAutoFilled = false // true only once "Use found names" has been clicked
    if (this.hasEmailTarget) this.validate()
  }

  disconnect() {
    clearTimeout(this.lookupTimer)
  }

  validate() {
    if (!this.hasEmailTarget || !this.hasFirstNameTarget || !this.hasLastNameTarget || !this.hasSubmitTarget) return
    const email = this.emailTarget.value.trim()
    const firstNamePresent = this.firstNameTarget.value.trim().length > 0
    const lastNamePresent = this.lastNameTarget.value.trim().length > 0
    const validEmail = this.isValidEmail(email)
    const verified = email === "" || email === this.verifiedEmail

    this.firstNameTarget.setCustomValidity(firstNamePresent ? "" : "First name is required.")
    this.lastNameTarget.setCustomValidity(lastNamePresent ? "" : "Last name is required.")
    this.emailTarget.setCustomValidity(validEmail ? "" : "Enter a valid email address.")
    this.firstNameTarget.classList.toggle("is-invalid", !firstNamePresent)
    this.lastNameTarget.classList.toggle("is-invalid", !lastNamePresent)
    this.emailTarget.classList.toggle("is-invalid", !validEmail)
    this.submitTarget.disabled = !(firstNamePresent && lastNamePresent && validEmail && verified)
  }

  lookupEmail() {
    clearTimeout(this.lookupTimer)
    this.validate() // typing invalidates the previous verification immediately
    this.lookupTimer = setTimeout(() => this.fetchExistingUser(), 300)
  }

  async fetchExistingUser() {
    if (!this.hasEmailTarget || !this.hasAlertTarget) return
    const email = this.emailTarget.value.trim()
    const token = ++this.requestToken

    if (!this.isValidEmail(email)) {
      this.hideAlert()
      this.resetNameFields()
      this.validate()
      return
    }

    try {
      const response = await fetch(this.verifyUrlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']")?.content,
        },
        body: JSON.stringify({ email }),
      })
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)

      const data = await response.json()

      // A later keystroke already started a newer lookup -- this result is stale.
      if (token !== this.requestToken) return

      if (data.exists) {
        this.showMatch(data.first_name, data.last_name)
      } else {
        this.hideAlert()
        this.resetNameFields()
      }
      this.verifiedEmail = email
    } catch (error) {
      console.error("Email lookup failed:", error)
      if (token !== this.requestToken) return
      this.verifiedEmail = null // keep Save disabled -- we couldn't confirm this email
      this.showError("Could not verify this email right now. Please try again.")
    } finally {
      if (token === this.requestToken) this.validate()
    }
  }

  isValidEmail(email) {
    if (email === "") return !this.requiredEmailValue
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  showMatch(foundFirstName, foundLastName) {
    const firstMatches = this.firstNameTarget.value.trim().toLowerCase() === foundFirstName.trim().toLowerCase()
    const lastMatches = this.lastNameTarget.value.trim().toLowerCase() === foundLastName.trim().toLowerCase()

    this.alertTarget.replaceChildren()
    const message = document.createElement("strong")
    message.textContent = "User found with this email."
    this.alertTarget.append(message, document.createElement("br"))
    this.appendComparison("First name", this.firstNameTarget.value.trim(), foundFirstName, firstMatches)
    this.appendComparison("Last name", this.lastNameTarget.value.trim(), foundLastName, lastMatches)

    const applyButton = document.createElement("button")
    applyButton.type = "button"
    applyButton.className = "btn btn-sm btn-outline-secondary mt-2"
    applyButton.textContent = "Use found names"
    applyButton.addEventListener("click", () => this.applyFoundNames(foundFirstName, foundLastName))
    this.alertTarget.append(applyButton)
    this.alertTarget.classList.remove("d-none")
  }

  appendComparison(label, entered, found, matches) {
    const row = document.createElement("div")
    const status = document.createElement("span")
    status.className = matches ? "text-success" : "text-danger"
    status.textContent = matches ? "matches" : "does not match"
    row.textContent = `${label} `
    row.append(status, ` (entered: "${entered || "(blank)"}", found: "${found}")`)
    this.alertTarget.append(row)
  }

  showError(message) {
    if (!this.hasAlertTarget) return
    this.alertTarget.replaceChildren()
    const node = document.createElement("div")
    node.className = "text-danger"
    node.textContent = message
    this.alertTarget.append(node)
    this.alertTarget.classList.remove("d-none")
  }

  applyFoundNames(firstName, lastName) {
    this.firstNameTarget.value = firstName
    this.lastNameTarget.value = lastName
    this.firstNameTarget.readOnly = true
    this.lastNameTarget.readOnly = true
    this.namesAutoFilled = true
    this.validate()
  }

  resetNameFields() {
    this.firstNameTarget.readOnly = false
    this.lastNameTarget.readOnly = false

    // Only clear values "Use found names" applied -- never touch what the user typed
    // themselves (e.g. they applied a match, then changed the email to something else).
    if (this.namesAutoFilled) {
      this.firstNameTarget.value = ""
      this.lastNameTarget.value = ""
      this.namesAutoFilled = false
    }
  }

  hideAlert() {
    this.alertTarget.replaceChildren()
    this.alertTarget.classList.add("d-none")
  }

  submit(event) {
    if (!this.hasSubmitTarget) return
    if (this.hasEmailTarget) this.emailTarget.value = this.emailTarget.value.trim()
    this.validate()
    if (this.submitTarget.disabled) event.preventDefault()
  }
}
